import { Image } from 'react-datocms';
import TickerItem from './TickerItem';
import { default as NextImage } from 'next/image';
import { StructuredText } from 'react-datocms/structured-text';
import type { CurrentAccountHeroFragment } from '@/utils/dato/generated';
import { customNodeRules, renderBlock } from '@/components/structuredText';
import { isEmptyDocument, type Document } from 'datocms-structured-text-utils';

const CurrentAccountHero = ({
  greyBoxContent,
  trustedByBlock,
  toggleGreyBox,
  heroContent,
  tickerTape,
  heroImage,
}: CurrentAccountHeroFragment) => (
  <div className="bg-[#EFFFF6]">
    <div className="pb-6 mb:pb-12 max-w-[540px] md:max-w-[1224px] mx-auto [&:has(+.band--color)]:mb-8 [&:has(+.band--color)]:md:mb-13">
      <div className={`flex flex-col md:flex-row md:items-stretch ${toggleGreyBox ? 'md:mb-7' : ''}`}>
        <div className={`px-4 mx-auto md:w-[60%] pt-7 ${toggleGreyBox ? 'pb-7' : ''} md:m-0 md:pt-10 lg:pt-13`}>
          {!isEmptyDocument(heroContent) ? (
            <StructuredText
              data={heroContent as unknown as Document}
              customNodeRules={customNodeRules}
              renderBlock={renderBlock}
            />
          ) : null}
        </div>
        <div className="relative flex flex-col md:justify-between">
          <div className="order-last md:order-first pt-6 md:pt-10 lg:pt-13">
            <Image data={heroImage.responsiveImage} className="z-10" priority={true} />
          </div>
          <div
            className="order-first md:order-last flex flex-wrap gap-4 items-center justify-center md:justify-end
          mt-4 px-4 md:px-0 md:mt-0 md:mr-8"
          >
            {trustedByBlock.map((block) => {
              if (block.__typename === 'TrustedbyRecord') {
                return (
                  <div
                    className="flex flex-col justify-between gap-1 bg-brand rounded-xl p-3 h-[76px] w-[calc(50%-0.5rem)] md:w-auto shrink-0"
                    key={block.id}
                  >
                    <div className="flex items-center gap-3 font-bold text-sm">
                      <NextImage src="/images/trust.png" alt="trust icon" height={16} width={16} />
                      <div>{block.title}</div>
                    </div>
                    <div className="text-[10px] leading-tight md:w-15">{block.subtitle}</div>
                  </div>
                );
              }
              if (block.__typename === 'TrustBadgesBlockRecord' && block.image?.url) {
                const img = (
                  <NextImage
                    height={block.image.height || 80}
                    width={block.image.width || 240}
                    alt={block.image.title || ''}
                    className="h-9 w-auto"
                    src={block.image.url}
                    priority={true}
                  />
                );
                return block.link ? (
                  <a
                    className="flex items-center justify-center bg-brand rounded-xl p-3 h-[76px] w-[calc(50%-0.5rem)] md:w-auto shrink-0"
                    rel="noopener noreferrer"
                    href={block.link}
                    target="_blank"
                    key={block.id}
                  >
                    {img}
                  </a>
                ) : (
                  <div
                    className="flex items-center justify-center bg-brand rounded-xl p-3 h-[76px] w-[calc(50%-0.5rem)] md:w-auto shrink-0"
                    key={block.id}
                  >
                    {img}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
      {toggleGreyBox ? (
        <div className="px-4 text-center md:text-left">
          <div className="bg-base-100 px-5 py-9 relative z-10 md:p-8 lg:pb-11 lg:p-13 rounded-xl md:flex md:gap-8 lg:gap-13">
            <div className="md:w-1/2 [&>.btn]:max-md:w-full [&>.btn]:mb-5 [&>.btn]:lg:my-7">
              {!isEmptyDocument(greyBoxContent) ? (
                <StructuredText customNodeRules={customNodeRules} renderBlock={renderBlock} data={greyBoxContent} />
              ) : null}
            </div>
            <div className="md:w-1/2 text-center">
              <div className="flex gap-6 mb-5 mt-9 md:mt-0 items-center justify-center">
                <div className="w-[134px] lg:w-[230px]">
                  <NextImage
                    src="https://images-static.trustpilot.com/api/logos/light-bg/logo.svg"
                    alt="Trustpilot logo"
                    className="w-full"
                    priority={true}
                    height="76"
                    width="242"
                  />
                </div>
                <div className="w-[134px] lg:w-[227px]">
                  <NextImage
                    src="https://images-static.trustpilot.com/api/stars/4.5/512x96.png"
                    alt="Average star score of 4.5 in trustpilot"
                    className="w-full"
                    priority={true}
                    height="43"
                    width="227"
                  />
                </div>
              </div>
              <span className="lg:text-lg">
                Rated excellent based on <span className="font-semibold">25,000+</span> reviews
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>

    {tickerTape.length > 0 ? (
      <div className="bg-[#CBFFE6] py-8 md:h-12 md:py-0 flex items-center px-6 md:px-12 md:mt-4">
        <div className="flex flex-col md:flex-row items-center justify-center w-full gap-8 md:gap-14">
          {tickerTape.map((item) => (
            <TickerItem subtitle={item.subtitle} title={item.title} key={item.id} />
          ))}
        </div>
      </div>
    ) : null}
  </div>
);

export default CurrentAccountHero;
